import _ from 'lodash'
import { useState } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { Trans } from '@lingui/macro'
import { Dialog, DialogTitle, DialogContent, Button, IconButton, Tooltip } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import { useCreatePlanningMutation } from '@/core/services/planning'
import { getApiErrorMessage } from '@/utils/errors'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

import { PlanningForm, PlanningFormData } from './PlanningForm'
import { serializePlanningDateRange } from '../lib/planningDates'

interface CreatePlanningDialogProps {
  visibility: 'facility' | 'network'
  visibilityId: string
}

export function CreatePlanningDialog({ visibility, visibilityId }: CreatePlanningDialogProps) {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const [isOpen, setIsOpen] = useState(false)

  const [createPlanning, { isLoading: isCreatingPlanning }] = useCreatePlanningMutation()

  const handleSubmit = async ({ dateRange, ...data }: PlanningFormData) => {
    try {
      await createPlanning({
        ...data,
        ...serializePlanningDateRange(dateRange.start, dateRange.end),
        visibility,
        // If we have default values for facility context, use them
        ...(visibility === 'facility' && {
          facility: visibilityId,
        }),
        // If we have default values for network context, use them
        ...(visibility === 'network' && {
          network: visibilityId,
        }),
      }).unwrap()
      enqueueSnackbar(t('page.planning.create.success', { defaultValue: 'Planning created successfully' }), { variant: 'success' })
      setIsOpen(false)
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.planning.create.error', { defaultValue: 'Failed to create planning' })), { variant: 'error' })
    }
  }

  return (
    <>
      <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setIsOpen(true)}>
        <Trans>Create Planning</Trans>
      </Button>
      <Dialog open={isOpen} onClose={() => setIsOpen(false)} fullScreen={isMobile} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between' }}>
          <Trans>Create planning</Trans>
          <Tooltip title={t('common:button.close.tooltip')}>
            <IconButton onClick={() => setIsOpen(false)}>
              <CloseRoundedIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          <PlanningForm isSubmitting={isCreatingPlanning} onSubmit={handleSubmit} />
        </DialogContent>
      </Dialog>
    </>
  )
}
